import React, { useEffect, useRef, useState } from "react";

const RisingEmbers = ({
	width = 300,
	height = 400,
	emberInterval = 100,
	initialEmbers = 20,
}) => {
	const [embers, setEmbers] = useState([]);
	const nextId = useRef(0);
	const intervalIdRef = useRef(null);

	const createEmber = () => {
		const size = Math.random() * 5 + 3;
		const left = Math.random() * width;
		const riseDuration = Math.random() * 2 + 4;
		const windDrift = Math.random() * 100 - 50;

		const ember = {
			id: nextId.current,
			style: {
				width: `${size}px`,
				height: `${size}px`,
				left: `${left}px`,
				"--rise-duration": `${riseDuration}s`,
				"--wind-drift": `${windDrift}px`,
			},
		};
		nextId.current += 1;
		return ember;
	};

	const startEmberInterval = () => {
		// Clear any existing interval first
		if (intervalIdRef.current) {
			clearInterval(intervalIdRef.current);
		}

		// Set up new interval for continuous ember creation
		intervalIdRef.current = setInterval(() => {
			setEmbers((prev) => [...prev, createEmber()]);
		}, emberInterval);
	};

	useEffect(() => {
		// Create initial batch of embers
		setEmbers(Array.from({ length: initialEmbers }, createEmber));

		// Handle visibility change
		const handleVisibilityChange = () => {
			if (document.hidden) {
				// Clear interval when tab is hidden
				if (intervalIdRef.current) {
					clearInterval(intervalIdRef.current);
					intervalIdRef.current = null;
				}
			} else {
				// Restart interval when tab becomes visible
				startEmberInterval();
			}
		};

		// Add visibility change listener
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Start initial interval
		startEmberInterval();

		// Cleanup
		return () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current);
			}
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			);
		};
	}, [initialEmbers, emberInterval]);

	// Remove embers after animation completes
	const handleAnimationEnd = (id) => {
		setEmbers((prev) => prev.filter((ember) => ember.id !== id));
	};

	return (
		<div
			style={{
				position: "relative",
				width: width,
				height: height,
				overflow: "hidden",
			}}
		>
			<style>
				{`
          .ember {
            position: absolute;
            bottom: -20px;
            background: radial-gradient(circle at center, #7B2CBF, #F3DFFF20);
            border-radius: 50%;
            opacity: 0;
            mix-blend-mode: screen;
            animation: rise var(--rise-duration) ease-out;
          }

          @keyframes rise {
            0% {
              transform: translateY(0) translateX(0);
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              transform: translateY(-400px) translateX(var(--wind-drift));
              opacity: 0;
            }
          }
        `}
			</style>
			{embers.map((ember) => (
				<div
					key={ember.id}
					className="ember"
					style={ember.style}
					onAnimationEnd={() => handleAnimationEnd(ember.id)}
				/>
			))}
		</div>
	);
};

export default RisingEmbers;
